import { Link, Stack, Typography } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { ConfirmationDialog } from '@components/ConfirmationDialog';
import ContentLoader from '@components/ContentLoader';
import Icon from '@components/Icon/Icon';

import type { FileManagementProps } from '@hooks/useFileManagement';
import { useFileManagement } from '@hooks/useFileManagement';

import { formatFilename } from '@utils/format';

import type { Attachment } from 'generated/types';

const messages = defineMessages({
  download: {
    id: 'attachments.download',
    defaultMessage: 'Download',
  },
  none: {
    id: 'attachments.none',
    defaultMessage: 'None',
  },
  deleteAttachment: {
    id: 'attachments.deleteAttachment',
    defaultMessage: 'Delete {filename}?',
  },
  deleteConfirmation: {
    id: 'GLOBAL.deleteConfirmation',
    defaultMessage: 'To confirm deletion, type {phrase} in the field.',
  },
  deletePhrase: {
    id: 'confirmation.deletePhrase',
    defaultMessage: 'DELETE',
  },
});

interface AttachmentListProps extends FileManagementProps {
  attachments?: Attachment[];
  can_delete?: boolean;
}

export default function AttachmentList({
  attachments = [],
  can_delete,
  resource,
  patient_id,
}: AttachmentListProps) {
  const { formatMessage } = useIntl();

  const { deleting, handleDelete, handleDownload, operating } =
    useFileManagement({
      attachments,
      resource,
      patient_id,
    });

  return (
    <ContentLoader loading={deleting} style={{ padding: 0 }}>
      {attachments.length ? (
        attachments.map((attachment, index) => {
          const filename = formatFilename(attachment.title);
          return (
            <ContentLoader
              lineCount={1}
              loading={operating[index]}
              style={{ padding: 0 }}
              key={`attachment-${attachment.id}`}
            >
              <Stack direction="row" spacing={2}>
                <Icon
                  color="text.secondary"
                  name="paperclip"
                  rotation={45}
                  style={{
                    marginRight: 5,
                    fontSize: 20,
                  }}
                  type="far"
                />
                <Typography
                  variant="body2"
                  sx={{
                    textWrap: 'wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {filename}
                </Typography>

                <Link
                  aria-label="download"
                  onClick={async () => handleDownload(attachment, index)}
                  variant="body1"
                >
                  {formatMessage(messages.download)}
                </Link>
                {can_delete ? (
                  <ConfirmationDialog
                    challenge={{
                      description: formatMessage(messages.deleteConfirmation, {
                        phrase: formatMessage(messages.deletePhrase),
                      }),
                      phrase: formatMessage(messages.deletePhrase),
                    }}
                    onConfirm={async () => handleDelete(attachment.id)}
                    title={formatMessage(messages.deleteAttachment, {
                      filename,
                    })}
                  >
                    {({ handleOpen }) => {
                      return <Icon name="trash" onClick={handleOpen} />;
                    }}
                  </ConfirmationDialog>
                ) : null}
              </Stack>
            </ContentLoader>
          );
        })
      ) : (
        <Typography variant="subtitle1">
          {formatMessage(messages.none)}
        </Typography>
      )}
    </ContentLoader>
  );
}
